import React from 'react';
import { MapPin, ArrowRight, Phone, CheckCircle } from 'lucide-react';
import Navigation from './Navigation';
import SEO from './SEO';
import Testimonials from './Testimonials';
import ContactForm from './ContactForm';
import Footer from './Footer';
import { faqSchema, breadcrumbSchema } from '../utils/schemas';
const LocationPageTemplate = ({
  city,
  state,
  slug,
  tagline,
  description,
  services,
  localInfo,
  faqs,
  schema,
}) => {
  const citySlug = slug || city.toLowerCase();
  const schemas = [
    ...(schema ? (Array.isArray(schema) ? schema : [schema]) : []),
    breadcrumbSchema([
      { name: 'Home', url: '/' },
      { name: city, url: `/locations/${citySlug}` },
    ]),
    ...(faqs
      ? [faqSchema(faqs.map((f) => ({ question: f.q, answer: f.a })))]
      : []),
  ];

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title={`Mortgage Broker in ${city} | ${state}`}
        description={`Expert mortgage brokers in ${city}, ${state}. ${tagline}`}
        path={`/locations/${citySlug}`}
        schema={schemas}
      />
      <Navigation />

      {/* Hero */}
      <section className="relative pt-32 pb-20 bg-gradient-to-br from-[#00594C] to-[#003d35]">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center text-white">
            <div className="inline-flex items-center space-x-2 bg-white/10 backdrop-blur-md px-5 py-2 rounded-full mb-6 border border-white/20">
              <MapPin className="w-4 h-4 text-[#99DF63]" />
              <span className="text-sm font-medium">{city}, {state}</span>
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-4">
              Mortgage Broker in <span className="text-[#99DF63]">{city}</span>
            </h1>
            <p className="text-xl md:text-2xl text-gray-200 mb-8">{tagline}</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="#book-appointment"
                className="inline-flex items-center justify-center space-x-2 bg-[#99DF63] text-[#00594C] px-8 py-4 rounded-full font-bold text-lg hover:bg-[#8BCF52] transition-all shadow-lg hover:shadow-xl transform hover:scale-105"
              >
                <span>Book a Consultation</span>
                <ArrowRight className="w-5 h-5" />
              </a>
              <a
                href="tel:+61468007056"
                className="inline-flex items-center justify-center space-x-2 bg-white/10 backdrop-blur-md text-white px-8 py-4 rounded-full font-bold text-lg hover:bg-white/20 transition-all border border-white/30"
              >
                <Phone className="w-5 h-5" />
                <span>0468 007 056</span>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* About */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl font-bold text-[#00594C] mb-6">
              Your Trusted Mortgage Broker in {city}
            </h2>
            <p className="text-lg text-gray-700 leading-relaxed">{description}</p>
          </div>
        </div>
      </section>

      {/* Services Available */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-bold text-[#00594C] text-center mb-4">
            Loan Services in {city}
          </h2>
          <p className="text-gray-600 text-center mb-12 max-w-2xl mx-auto">
            We offer a full range of lending solutions for {city} residents and businesses
          </p>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-5xl mx-auto">
            {services.map((service, i) => {
              const SvcIcon = service.icon;
              return (
                <a
                  key={i}
                  href={service.href}
                  className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 hover:shadow-md hover:border-[#99DF63]/30 transition-all group"
                >
                  <SvcIcon className="w-8 h-8 text-[#00594C] mb-3 group-hover:text-[#99DF63] transition-colors" />
                  <h3 className="font-semibold text-[#00594C] mb-1">{service.name}</h3>
                  <p className="text-gray-600 text-sm">{service.description}</p>
                </a>
              );
            })}
          </div>
        </div>
      </section>

      {/* Local Info */}
      {localInfo && (
        <section className="py-16 bg-white">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl font-bold text-[#00594C] text-center mb-12">
              {city} Property Market
            </h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-5xl mx-auto">
              {localInfo.map((info, i) => (
                <div key={i} className="flex items-start space-x-3">
                  <div className="w-6 h-6 bg-[#99DF63]/20 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <CheckCircle className="w-4 h-4 text-[#00594C]" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-[#00594C] mb-1">{info.title}</h3>
                    <p className="text-gray-600 text-sm">{info.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* FAQs */}
      {faqs && (
        <section className="py-16 bg-gray-50">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl md:text-4xl font-bold text-[#00594C] text-center mb-12">
              {city} Mortgage FAQs
            </h2>
            <div className="max-w-3xl mx-auto space-y-4">
              {faqs.map((faq, i) => (
                <details key={i} className="bg-white rounded-xl border border-gray-100 group">
                  <summary className="px-6 py-4 cursor-pointer font-semibold text-[#00594C] hover:text-[#99DF63] transition-colors list-none flex items-center justify-between">
                    {faq.q}
                    <svg className="w-5 h-5 transition-transform group-open:rotate-180 flex-shrink-0 ml-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </summary>
                  <div className="px-6 pb-4 text-gray-600">{faq.a}</div>
                </details>
              ))}
            </div>
          </div>
        </section>
      )}

      <Testimonials />

      {/* General Advice Warning */}
      <section className="py-8 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto bg-white border border-amber-200 rounded-xl p-6">
            <p className="text-sm text-gray-600">
              <strong className="text-gray-800">General Advice Warning:</strong> The information provided on this page is general in nature and does not take into account your objectives, financial situation or needs. You should consider whether it is appropriate for you before acting on it.
            </p>
          </div>
        </div>
      </section>

      <ContactForm />
      <Footer />
    </div>
  );
};

export default LocationPageTemplate;
